import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { useDispatch } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import isValidDate from 'date-fns/isValid';
import { Box } from '@daedalus/atlas/helpers/Box';
import { InputButton } from '@daedalus/atlas/helpers/InputButton';
import { Icon } from '@daedalus/atlas/Icon';
import { Text } from '@daedalus/atlas/Text';
import { Tooltip, TooltipPlacement } from '@daedalus/atlas/Tooltip';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { MiddayDate } from '@daedalus/core/src/utils/date';
import { guestPickerMessages } from '../GuestPicker/guestPickerMessages';
type Props = {
  /** Check in date */
  checkIn: MiddayDate;
  /** Check out date */
  checkOut: MiddayDate;
  /** Destination name */
  destination: string;
  /** Number of guests */
  numberOfGuests: number;
  /** Number of rooms */
  numberOfRooms: number;
  /** Click callback */
  onClick: () => void;
  /** Whether to show the compacted variant */
  isCompact?: boolean;
  /** Whether the rooms count and label is shown */
  isRoomsLabelShown?: boolean;
  /** Whether the Summary Button is rounded */
  isRounded?: boolean;
  /** Whether the color used for borders and icons is the default or neutral */
  isNeutralColor?: boolean;
  // Added as part of ab519a8e-date-confirmation-tooltip
  /** Whether user landed directly on the Search Page */
  isUserLandingDirectlyOnSRP?: boolean;
};
// TODO: Remove getLinariaClassName when migrating to linaria
const _exp = /*#__PURE__*/() => InputButton;
const SummaryButton = /*#__PURE__*/styled(_exp())({
  name: "SummaryButton",
  class: "s399y79",
  propsAsIs: true
});
const ContentWrapper = /*#__PURE__*/styled('span')({
  name: "ContentWrapper",
  class: "c9e75ga",
  propsAsIs: false
});
const DestinationText = /*#__PURE__*/styled('span')({
  name: "DestinationText",
  class: "d1256cu3",
  propsAsIs: false
});
const GuestsDateWrapper = /*#__PURE__*/styled('span')({
  name: "GuestsDateWrapper",
  class: "g1q7fpp9",
  propsAsIs: false
});
const CompactBullet = /*#__PURE__*/styled('span')({
  name: "CompactBullet",
  class: "c1tfx3sc",
  propsAsIs: false
});

// Added as part of ab519a8e-date-confirmation-tooltip
const DateConfirmationWrapper = /*#__PURE__*/styled('div')({
  name: "DateConfirmationWrapper",
  class: "dpd96v1",
  propsAsIs: false
});
const ToolTipAnchor = /*#__PURE__*/styled('div')({
  name: "ToolTipAnchor",
  class: "t1hyt486",
  propsAsIs: false
});
// END Added as part of ab519a8e-date-confirmation-tooltip

const ShortFormattedDate = ({
  value
}: {
  value: Date;
}): React.ReactElement => <FormattedDate value={value} month="short" day="2-digit" />;

// Added as part of ab519a8e-date-confirmation-tooltip
const DateConfirmationTooltip: React.FC<{
  children: React.ReactNode;
  isUserLandingDirectlyOnSRP: boolean;
  isLoaded: boolean;
}> = ({
  children,
  isUserLandingDirectlyOnSRP,
  isLoaded
}) => {
  const [isInteracted, setIsInteracted] = useState(false);
  const dispatch = useDispatch();
  const anchorRef = useRef(null);
  const showTooltip = isUserLandingDirectlyOnSRP && isLoaded && !isInteracted;
  const closeTooltip = useCallback(() => setIsInteracted(true), []);
  const handleCloseTooltip = useCallback(event => {
    if (!window.Osano?.cm?.dialogOpen) {
      event?.preventDefault();
    }
    closeTooltip();
  }, [closeTooltip]);
  useEffect(() => {
    if (showTooltip) {
      document.addEventListener('touchend', handleCloseTooltip);
      document.addEventListener('scroll', handleCloseTooltip);
    }
    return () => {
      document.removeEventListener('touchend', handleCloseTooltip);
      document.removeEventListener('scroll', handleCloseTooltip);
    };
  }, [handleCloseTooltip, showTooltip]);
  useEffect(() => {
    if (isUserLandingDirectlyOnSRP) {
      dispatch(trackEvent({
        category: Category.System,
        entity: Entity.SearchDateToolTip,
        action: Action.Displayed
      }));
    }
  }, [isUserLandingDirectlyOnSRP, dispatch]);
  if (!showTooltip) {
    return <>{children}</>;
  }
  return <>
      <Tooltip isInline={false} isOpen={Boolean(!isInteracted && anchorRef.current)} anchor={anchorRef.current} placement={TooltipPlacement.BottomEnd} onClose={closeTooltip} content={<Box width="247px">
            <Text variant="bodyS">
              <FormattedMessageWrapper id="confirmDates" defaultMessage="Remember to double check your dates" />
            </Text>
          </Box>}>
        {/* Empty element has to be there otherwise there gonna be some issues with the content that being rendered by Tooltip */}
        <div></div>
      </Tooltip>
      <DateConfirmationWrapper className="custom-wrapper">
        {children}
        <ToolTipAnchor ref={anchorRef} />
      </DateConfirmationWrapper>
    </>;
};
export const SearchSummaryButton = ({
  destination,
  checkIn,
  checkOut,
  numberOfGuests,
  numberOfRooms,
  onClick,
  isCompact = false,
  isRoomsLabelShown = true,
  isRounded = true,
  isNeutralColor = false,
  isUserLandingDirectlyOnSRP = false
}: Props) => {
  const isDatesValid = isValidDate(checkIn) && isValidDate(checkOut);
  return <DateConfirmationTooltip isUserLandingDirectlyOnSRP={isUserLandingDirectlyOnSRP} isLoaded={Boolean(isDatesValid)}>
      <SummaryButton id="SearchSummary" onClick={onClick} dataId="SearchSummary" className={cx(isCompact && 'isCompact', isNeutralColor && 'isNeutralColor')} icon={<Icon name="Search" size="md" colorPath={isNeutralColor ? undefined : 'content.interactive.c700'} />} isRounded={isRounded}>
        <ContentWrapper className={cx(isCompact && 'isCompact')}>
          <DestinationText data-id="DestinationValue">
            {destination || <FormattedMessageWrapper id="whereAreYouGoing" defaultMessage="Where are you going?" />}
          </DestinationText>

          {isCompact && <CompactBullet>{'\u00A0·\u00A0'}</CompactBullet>}

          {isDatesValid && <GuestsDateWrapper className={cx(isCompact && 'isCompact', 'chromatic-ignore')}>
              <span data-id="CheckInValue">
                <ShortFormattedDate value={checkIn} />
              </span>
              {' - '}
              <span data-id="CheckOutValue">
                <ShortFormattedDate value={checkOut} />
              </span>
              {!isCompact && <>
                  <span data-id="GuestValue">{' · '}</span>
                  <FormattedMessageWrapper {...guestPickerMessages.guest} values={{
              count: numberOfGuests
            }} />
                  {isRoomsLabelShown && <>
                      <span data-id="RoomValue">{', '}</span>
                      <FormattedMessageWrapper {...guestPickerMessages.room} values={{
                count: numberOfRooms
              }} />
                    </>}
                </>}
            </GuestsDateWrapper>}
        </ContentWrapper>
      </SummaryButton>
    </DateConfirmationTooltip>;
};

require("./SearchSummaryButton.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SearchSummaryButton.tsx");